import { useState } from "react";
import { Loader2, Send, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { SUPPORT_EMAIL } from "@/lib/pricing";

export const fieldCls = "w-full rounded-xl bg-white/[0.04] border border-gold-500/20 px-4 py-3 font-ui text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-gold-300/60 transition-colors";
export const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="block font-ui text-xs font-semibold tracking-wide text-slate-400 mb-1.5">{children}</label>
);
export const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return "Please check the highlighted fields and try again.";
  return d || fallback;
};

const CATEGORIES = [
  ["account", "Account & sign-in"], ["billing", "Billing & credits"], ["book", "A book I'm creating"],
  ["publishing", "Publishing / KDP files"], ["other", "Something else"],
];
const EMPTY = { name: "", email: "", category: "book", subject: "", message: "", website: "" };

const TicketDone = ({ number, onAgain }) => (
  <div data-testid="support-ticket-success" className="rounded-2xl border border-emerald-400/30 bg-emerald-400/[0.06] p-6">
    <CheckCircle2 className="w-6 h-6 text-emerald-300 mb-3" />
    <h3 className="font-ui text-lg font-semibold text-slate-100">Ticket received</h3>
    <p className="font-read text-slate-300 mt-2">Your ticket number is <strong data-testid="support-ticket-number" className="text-gold-200">{number}</strong>. We've emailed you a copy and will reply to the address you gave us.</p>
    <button data-testid="support-ticket-again" onClick={onAgain} className="mt-4 font-ui text-sm text-gold-200 hover:text-gold-100 transition-colors">Open another ticket</button>
  </div>
);

export const SupportTicketForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await axios.post(`${API}/support/tickets`, f);
      setDone(data.ticket_number); setF(EMPTY);
    } catch (e2) {
      setErr(errText(e2, `Something went wrong. Please email ${SUPPORT_EMAIL}.`));
    } finally { setBusy(false); }
  };
  if (done) return <div className="mt-10 max-w-2xl"><TicketDone number={done} onAgain={() => setDone(null)} /></div>;
  return (
    <form data-testid="support-ticket-form" onSubmit={submit} className="mt-10 max-w-2xl rounded-2xl border border-gold-500/15 bg-white/[0.02] p-6 sm:p-8 space-y-5">
      <div className="grid sm:grid-cols-2 gap-5">
        <div><Label htmlFor="t-name">Your name</Label><input id="t-name" data-testid="support-name-input" required maxLength={120} value={f.name} onChange={set("name")} className={fieldCls} /></div>
        <div><Label htmlFor="t-email">Email</Label><input id="t-email" data-testid="support-email-input" type="email" required value={f.email} onChange={set("email")} className={fieldCls} /></div>
      </div>
      <div><Label htmlFor="t-cat">What's it about?</Label>
        <select id="t-cat" data-testid="support-category-select" value={f.category} onChange={set("category")} className={fieldCls}>
          {CATEGORIES.map(([v, l]) => <option key={v} value={v} className="bg-navy-900">{l}</option>)}
        </select>
      </div>
      <div><Label htmlFor="t-subj">Subject</Label><input id="t-subj" data-testid="support-subject-input" required minLength={3} maxLength={160} value={f.subject} onChange={set("subject")} className={fieldCls} /></div>
      <div><Label htmlFor="t-msg">How can we help?</Label><textarea id="t-msg" data-testid="support-message-input" required minLength={10} maxLength={5000} rows={6} value={f.message} onChange={set("message")} className={fieldCls} placeholder="Tell us what happened and what you expected." /></div>
      <input tabIndex={-1} autoComplete="off" aria-hidden="true" value={f.website} onChange={set("website")} className="hidden" name="website" />
      {err && <p data-testid="support-ticket-error" className="font-ui text-sm text-red-300">{err}</p>}
      <button data-testid="support-ticket-submit" disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-gold-300 hover:bg-gold-200 text-stone-950 font-ui font-semibold text-sm px-6 py-3 transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit ticket
      </button>
    </form>
  );
};
